import { smallTextBoxWidth } from "odyssey-shared";
import { MAP_SCALE, type TilePoint } from "./geometry.ts";

/**
 * Which place names the world map draws (ADR-100's note of 2026-10-14, decision 3). A name is drawn beside its mark
 * only for the mark under the pointer, the mark with keyboard focus, the place the pin was set on and the
 * `NAMED_NEAREST` places nearest the character; every other mark shows its name as a tooltip. Names are taken in that
 * order, the nearer first within the nearest, and one whose box meets a name already taken is not drawn, so two names
 * never overlap and the one nearer the character keeps its own. A name that is not held is also dropped when its box lies
 * over the mark of a place nearer the character than itself, so a name never reads as part of another place's mark. The box of a name is estimated from its characters, so
 * the rule is pure and the page measures nothing. The cost is one pass over the places however many there are.
 */

/**
 * How many of the places nearest the character have their names drawn.
 */
export const NAMED_NEAREST = 3;

/**
 * The height of a name's line, in logical pixels: the small pixel face's line.
 */
export const NAME_HEIGHT = 9;

/**
 * The gap between a mark and its name, in logical pixels.
 */
export const NAME_GAP = 2;

/**
 * The marks' sizes in logical pixels (the amendment's decision 26): the character 10, a shrine 10,
 * the pin 7 by 8 with its tip on the spot, and the plaque of an edge arrow.
 */
export const MARK_SIZES = { you: 10, shrine: 10, party: 6, pinWidth: 7, pinHeight: 8, plaque: 16 } as const;

/**
 * The size of each kind of place's mark; a later kind joins by a row here.
 */
const PLACE_MARK_SIZES: Readonly<Record<string, number>> = { shrine: MARK_SIZES.shrine, waypoint: MARK_SIZES.shrine, party: MARK_SIZES.party };

/**
 * The size of a place's mark.
 *
 * @param kind The place's kind.
 * @returns A shrine's size, the size of every kind of place so far.
 */
export function markSize(kind: string): number {
  return PLACE_MARK_SIZES[kind] ?? MARK_SIZES.shrine;
}

const NO_KEYS: ReadonlySet<string> = new Set();

/**
 * A place as the choice of names reads it.
 */
export interface NameCandidate {
  readonly key: string;
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly size: number;
}

/**
 * What the player holds: the key of the mark under the pointer, of the mark with focus, and the pin's point.
 */
export interface NameHolds {
  readonly hover: string | null;
  readonly focus: string | null;
  readonly pin: TilePoint | null;
}

/**
 * A rectangle in picture pixels.
 */
export interface NameBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Where a place's name would lie, in picture pixels: to the right of its mark, centred on it.
 *
 * @param place The place.
 * @returns The box.
 */
export function nameBox(place: NameCandidate): NameBox {
  const left = Math.round(place.x * MAP_SCALE - place.size / 2) + place.size + NAME_GAP;
  const top = Math.round(place.y * MAP_SCALE - place.size / 2) + Math.round(place.size / 2) - Math.floor(NAME_HEIGHT / 2);
  return { left, top, width: smallTextBoxWidth(place.name), height: NAME_HEIGHT };
}

/**
 * Whether two boxes share any area.
 *
 * @param a One box.
 * @param b The other.
 * @returns True when they overlap.
 */
function meets(a: NameBox, b: NameBox): boolean {
  return a.left < b.left + b.width && b.left < a.left + a.width && a.top < b.top + b.height && b.top < a.top + a.height;
}

/**
 * Where a place's mark lies, in picture pixels, centred on the place.
 *
 * @param place The place.
 * @returns The box.
 */
function markBox(place: NameCandidate): NameBox {
  return { left: Math.round(place.x * MAP_SCALE - place.size / 2), top: Math.round(place.y * MAP_SCALE - place.size / 2), width: place.size, height: place.size };
}

/**
 * Chooses the names to draw.
 *
 * @param places Every known place, in any order.
 * @param from The character's feet in tiles.
 * @param holds The pointer's mark, the focused mark and the pin.
 * @param nearest How many of the nearest places are named; `NAMED_NEAREST` unless given.
 * @param always The keys of places that are named whatever their distance, as a party member is (ADR-100 decision 10);
 *   they take no part in the count of the nearest.
 * @returns The keys of the places whose names are drawn: the pointer's, the focus's, the pin's, those that are always
 *   named, then the nearest by distance. A held key that matches no place is ignored.
 */
export function chooseNames(places: readonly NameCandidate[], from: TilePoint, holds: NameHolds, nearest: number = NAMED_NEAREST, always: ReadonlySet<string> = NO_KEYS): readonly string[] {
  const order: NameCandidate[] = [];
  const take = (place: NameCandidate | undefined): void => {
    if (place !== undefined && !order.includes(place)) {
      order.push(place);
    }
  };
  take(holds.hover === null ? undefined : places.find((place) => place.key === holds.hover));
  take(holds.focus === null ? undefined : places.find((place) => place.key === holds.focus));
  const pin = holds.pin;
  take(pin === null ? undefined : places.find((place) => place.x === pin.x && place.y === pin.y));
  for (const place of places) {
    if (always.has(place.key)) {
      take(place);
    }
  }
  const held = new Set(order);
  const best: { readonly place: NameCandidate; readonly distance: number }[] = [];
  for (const place of places) {
    if (always.has(place.key)) {
      continue;
    }
    const distance = Math.hypot(place.x - from.x, place.y - from.y);
    let at = best.length;
    while (at > 0 && (best[at - 1] as { distance: number }).distance > distance) {
      at -= 1;
    }
    if (at < nearest) {
      best.splice(at, 0, { place, distance });
      if (best.length > nearest) {
        best.pop();
      }
    }
  }
  for (const entry of best) {
    take(entry.place);
  }
  const named: NameCandidate[] = [];
  const boxes: NameBox[] = [];
  for (const place of order) {
    const box = nameBox(place);
    const distance = Math.hypot(place.x - from.x, place.y - from.y);
    const overMark = !held.has(place) && places.some((other) => other !== place && Math.hypot(other.x - from.x, other.y - from.y) < distance && meets(markBox(other), box));
    if (!overMark && !boxes.some((taken) => meets(taken, box))) {
      named.push(place);
      boxes.push(box);
    }
  }
  return named.map((place) => place.key);
}
